import React, { Fragment, lazy, Suspense, useEffect } from "react";
import { API_URL, MONGOURL } from "@data/GlobalData.js";
import { useGenericStore } from "@customHooks/useGenericStore.js";
import {
  CFW_ERROR,
  CFW_GLOBAL,
  CFW_REFRESHER,
  DIALOGHOLDER,
  PROCESS_ADD,
  PROCESS_EDIT,
  PROCESS_SELECT,
  REGEXP_REMOVETAGS,
  Response_SelectedTranslation,
  Response_SiteMapUrlData,
  Response_TranslationItem,
} from "@genericComponents/CFW.js";
import { Clone } from "@genericComponents/Common.js";
import Loading from "@genericComponents/Loading.js";
import {
  MaintainDialog,
  MaintainTranslations,
} from "@genericComponents/PFW.js";
import { PostData } from "@genericComponents/SFW.js";
import {
  GetState,
  GetStateField,
  RemoveState,
  SetState,
  UpdateStateField,
  ValildateEntries,
} from "@genericComponents/StoreToolkit.js";
import DataHTML from "@controls/DataEntry/DataHTML/index.js";
import GroupButtons from "@controls/GroupButtons.js";
import Label from "@controls/Label.js";
import LanguageEntry from "@controls/LanguageEntry.js";
const useProductLanguageCode =
  await import("@customHooks/useProductLanguageCode.js").then(
    (mod) => mod.useProductLanguageCode,
  );
const LanguagesData = await import("@data/LanguagesData.js").then(
  (mod) => mod.LanguagesData,
);
const HydrationLoadScript = lazy(
  () => import("@controls/HydrationLoadScripts.js"),
);
const InputTranslation = lazy(() => import("@controls/InputTranslation.js"));
const GetDictionaryStorage =
  await import("@asyncImports/GetDictionaryStorage.js").then(
    (mod) => mod.GetDictionaryStorage,
  );
const GUID = await import("@asyncImports/GUID.js").then((mod) => mod.GUID);
const cssImporter = await import("@asyncImports/cssImporter.js").then(
  (mod) => mod.cssImporter,
);
const GenerateStateEntry =
  await import("@asyncImports/GenerateStateEntry.js").then(
    (mod) => mod.GenerateStateEntry,
  );
const MatchingFieldsOnly =
  await import("@asyncImports/MatchingFieldsOnly.js").then(
    (mod) => mod.MatchingFieldsOnly,
  );
const GetFirstState = await import("@asyncImports/GetFirstState.js").then(
  (mod) => mod.GetFirstState,
);
const MakeTranslation = () => {
  const Response_TranslateFromEnglishOpenAI = "TranslateFromEnglishOpenAI";
  const TranslateFromEnglishOpenAI = {
    LanguageCode: null,
    Text: null,
  };
  const refresherState = `${CFW_REFRESHER}-translation`;
  const { languageCode } = useProductLanguageCode();
  const selectedTranslation = useGenericStore(Response_SelectedTranslation);
  const ClearWindowTranslation = () => {
    RemoveState(Response_SelectedTranslation);
    if (window.selectedTranslation) {
      delete window.selectedTranslation;
    }
  };
  const refresher = useGenericStore(refresherState);
  const { entry, error, list } = GenerateStateEntry(
    Response_TranslationItem,
    MaintainTranslations,
  );
  const Retrieve = (languageCodeParam = null, description = null) => {
    window.selectedTranslation.Process = PROCESS_SELECT;
    if (languageCodeParam) {
      window.selectedTranslation.Filter.LanguageCode = languageCodeParam;
      window.selectedTranslation.LanguageCode = languageCodeParam;
    }
    if (window.selectedTranslation.Filter._id) {
      delete window.selectedTranslation.Filter._id;
    }
    if (window.selectedTranslation._id) {
      delete window.selectedTranslation._id;
    }
    if (window.selectedTranslation.TranslationDescription) {
      delete window.selectedTranslation.TranslationDescription;
    }
    PostData(
      [MONGOURL, "TranslationData"].join(""),
      window.selectedTranslation,
      Response_TranslationItem,
      () => {
        const results =
          GetFirstState(Response_TranslationItem) ?? window.selectedTranslation;
        let param = Clone(MaintainTranslations);
        param = MatchingFieldsOnly(results, param);
        param.Filter = MatchingFieldsOnly(results, param.Filter);
        param.Filter.TranslationDescription = null;
        if (languageCodeParam) {
          param.LanguageCode = languageCodeParam;
          param.Filter.LanguageCode = languageCodeParam;
        }
        if (!description && results.TranslationDescription) {
          description = results.TranslationDescription;
          description = new DOMParser().parseFromString(
            description,
            "text/html",
          ).body.innerHTML;
          description = description ? description : "";
        }
        if (description) {
          param.TranslationDescription = description;
        }
        param.Process = param._id ? PROCESS_EDIT : PROCESS_ADD;
        if (!param._id) {
          param._id = `_${GUID()}`;
        }
        SetState(entry.state, { ...param });
        SetState(list.state, { ...param });
        SetState(refresherState, true);
      },
    );
  };
  const Save = () => {
    const isValid = ValildateEntries(error.state, CFW_ERROR);
    if (!isValid) {
      const dialogParam = { ...MaintainDialog };
      SetState(DIALOGHOLDER, dialogParam);
    } else {
      const param = GetState(entry.state);
      if (param.TranslationDescription.substring(0, 3) === "<p>") {
        param.TranslationDescription =
          param.TranslationDescription.substring(3);
        param.TranslationDescription = param.TranslationDescription.substring(
          0,
          param.TranslationDescription.length - 4,
        );
      }
      PostData(
        [MONGOURL, "TranslationData"].join(""),
        param,
        Response_TranslationItem,
        () => {
          RemoveState(entry.state);
          RemoveState(list.state);
          ClearWindowTranslation();
        },
      );
    }
  };
  const TranslateToOtherLanguage = () => {
    const languageCodeParam = GetStateField(
      list.state,
      "LanguageCode",
      languageCode,
    );
    const translateParam = Clone(TranslateFromEnglishOpenAI);
    translateParam.LanguageCode = languageCodeParam;
    const description = GetStateField(entry.state, "TranslationDescription");
    translateParam.Text = description.replace(REGEXP_REMOVETAGS, "");
    PostData(
      [API_URL, "TranslateFromEnglishOpenAI"].join(""),
      translateParam,
      Response_TranslateFromEnglishOpenAI,
      () => {
        const translatedResult = GetState(Response_TranslateFromEnglishOpenAI);
        ClearWindowTranslation();
        RemoveState(refresherState);
        Retrieve(languageCodeParam, translatedResult.TranslatedText);
      },
    );
  };
  const languages = GetDictionaryStorage(
    LanguagesData,
    "LanguageCode",
    "LanguageName",
  ).filter((lang) => lang?.Key !== "en");
  useEffect(() => {
    if (refresher && !GetStateField(list.state, "LanguageCode")) {
      UpdateStateField(
        list.state,
        "LanguageCode",
        languages?.length ? languages[0]?.Key : null,
      );
    }
    if (selectedTranslation && !refresher) {
      Retrieve(languageCode);
    }
    return () => {};
  }, [selectedTranslation]);
  return selectedTranslation ? (
    <Suspense fallback={<Loading />}>
      <HydrationLoadScript
        postEvt={() => {
          import("@css/fieldsetStyles.css");
          import("@css/makeTranslationStyles.css");
          cssImporter("ui-icon-find", "ui-icon-switch-white");
        }}
      />
      <InputTranslation
        hdr={GetStateField(Response_SiteMapUrlData, "Title")}
        pcb={Save}
        ncb={() => {
          RemoveState(entry.state);
          RemoveState(list.state);
          ClearWindowTranslation();
        }}
      >
        <LanguageEntry
          state={entry.state}
          required
          onChange={() => {
            const param = GetState(entry.state);
            param.Filter.LanguageCode = param.LanguageCode;
            SetState(entry.state, param);
          }}
          defaultselected={GetStateField(entry.state, "LanguageCode")}
        />
        <GroupButtons>
          <button
            type="button"
            onClick={() => {
              RemoveState(refresherState);
              Retrieve(
                GetStateField(entry.state, "LanguageCode", languageCode),
              );
            }}
            className="button-icon ui-icon-find"
          >
            <Label category={CFW_GLOBAL} tranVal="52" />
          </button>
        </GroupButtons>
        {window.selectedTranslation.Filter.LanguageCode === "en" && (
          <fieldset className="makeTranslationEntry">
            <legend className="makeTranslationEntryLegend">
              <Label category={CFW_GLOBAL} tranVal="687" isPlain={true} />
            </legend>
            <LanguageEntry state={list.state} required data={languages} />
            <button
              type="button"
              className="primary button-icon ui-icon-switch-white makeTranslationEntryButton"
              onClick={() => {
                const results = GetState(Response_TranslationItem);
                if (results?.length && results[0]?.TranslationDescription) {
                  TranslateToOtherLanguage();
                }
              }}
            >
              <Label category={CFW_GLOBAL} tranVal="54" />
            </button>
          </fieldset>
        )}
        {refresher ? (
          <DataHTML
            name="TranslationDescription"
            state={entry.state}
            tran="74"
            required
          />
        ) : (
          <Fragment />
        )}
      </InputTranslation>
    </Suspense>
  ) : (
    <Fragment />
  );
};
export default MakeTranslation;
